import { Container, ContentContainer } from "@src/core/styles";
import type { FC, ChangeEvent } from "react";
import { useState, useEffect, useRef } from "react";
import { ContentWrapper } from "../home-page/dashboard.styles";
import { TitleText, DescriptionText } from "../price-page/price-page.styles";
import {
  JsonValidatorContainer,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  IndentLabel,
  IndentSelector,
  TwoPanelLayout,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelActions,
  ActionButton,
  ErrorMessage,
  SuccessMessage,
  CopyButton,
  StatsContainer,
  StatItem,
  CodeMirrorWrapper,
} from "./json-validator-page.styles";
import { enqueueSnackbar } from "notistack";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";

declare global {
  interface Window {
    ym?: (counterId: number, method: string, target: string, params?: any) => void;
  }
}

export const JsonValidatorPage: FC = () => {
  const [jsonInput, setJsonInput] = useState<string>("");
  const [formattedJson, setFormattedJson] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [indentSize, setIndentSize] = useState<2 | 4>(2);
  const [stats, setStats] = useState<{
    size: number;
    lines: number;
    keys: number;
    depth: number;
  } | null>(null);
  const [errorPosition, setErrorPosition] = useState<{ line: number; column: number } | null>(
    null
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.ym) {
      try {
        window.ym(96258803, "hit", window.location.href, {
          title: "JSON Beautifier & Validator",
          referer: document.referrer,
        });
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.log("Yandex Metrika not available in development");
        }
      }
    }
  }, []);

  // Real-time validation with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!jsonInput.trim()) {
        setIsValid(null);
        setError("");
        setErrorPosition(null);
        return;
      }
      try {
        JSON.parse(jsonInput);
        setIsValid(true);
        setError("");
        setErrorPosition(null);
      } catch (e) {
        setIsValid(false);
        if (e instanceof SyntaxError) {
          const msg = e.message;
          const posMatch = msg.match(/position (\d+)/i);
          if (posMatch) {
            const pos = parseInt(posMatch[1], 10);
            const lines = jsonInput.substring(0, pos).split("\n");
            const line = lines.length;
            const column = lines[lines.length - 1].length;
            setErrorPosition({ line, column });
            setError(`Строка ${line}, позиция ${column}: ${msg}`);
          } else {
            setError(msg);
            setErrorPosition(null);
          }
        } else if (e instanceof Error) {
          setError(e.message);
          setErrorPosition(null);
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [jsonInput]);

  const countKeys = (obj: any): number => {
    if (typeof obj !== "object" || obj === null) return 0;
    if (Array.isArray(obj)) return obj.reduce((sum: number, item: any) => sum + countKeys(item), 0);
    return (
      Object.keys(obj).length +
      Object.values(obj).reduce((sum: number, value: any) => sum + countKeys(value), 0)
    );
  };

  const getDepth = (obj: any): number => {
    if (typeof obj !== "object" || obj === null) return 0;
    const children = Object.values(obj);
    if (children.length === 0) return 1;
    return 1 + Math.max(...children.map(getDepth));
  };

  const handleFormat = () => {
    if (!jsonInput.trim()) return;
    try {
      const parsed = JSON.parse(jsonInput);
      const formatted = JSON.stringify(parsed, null, indentSize);
      setFormattedJson(formatted);
      setStats({
        size: new Blob([JSON.stringify(parsed)]).size,
        lines: formatted.split("\n").length,
        keys: countKeys(parsed),
        depth: getDepth(parsed),
      });
      enqueueSnackbar("JSON отформатирован", { variant: "success", preventDuplicate: true });
    } catch {
      // errors already shown via real-time validation
    }
  };

  const handleMinify = () => {
    if (!jsonInput.trim()) return;
    try {
      const parsed = JSON.parse(jsonInput);
      const minified = JSON.stringify(parsed);
      setFormattedJson(minified);
      setStats({
        size: new Blob([minified]).size,
        lines: 1,
        keys: countKeys(parsed),
        depth: getDepth(parsed),
      });
      enqueueSnackbar("JSON минифицирован", { variant: "success", preventDuplicate: true });
    } catch {
      // errors already shown via real-time validation
    }
  };

  const handleCopy = async () => {
    if (!formattedJson) return;
    try {
      await navigator.clipboard.writeText(formattedJson);
      enqueueSnackbar("JSON скопирован в буфер обмена", {
        variant: "success",
        preventDuplicate: true,
      });
    } catch {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = formattedJson;
        textArea.style.cssText = "position:fixed;left:-999999px;top:-999999px;";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand("copy");
        textArea.remove();
        if (successful) {
          enqueueSnackbar("JSON скопирован в буфер обмена", {
            variant: "success",
            preventDuplicate: true,
          });
        } else {
          throw new Error("copy failed");
        }
      } catch {
        enqueueSnackbar("Ошибка при копировании. Скопируйте вручную", {
          variant: "error",
          preventDuplicate: true,
        });
      }
    }
  };

  const handleDownload = () => {
    if (!formattedJson) return;
    const blob = new Blob([formattedJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "formatted.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    setJsonInput("");
    setFormattedJson("");
    setError("");
    setIsValid(null);
    setStats(null);
    setErrorPosition(null);
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setJsonInput(content);
      setFormattedJson("");
      setStats(null);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <Container>
      <ContentContainer>
        <ContentWrapper>
          <JsonValidatorContainer>
            <TitleText>JSON Beautifier & Validator</TitleText>
            <DescriptionText>
              Вставьте или введите JSON для валидации и форматирования. Инструмент
              проверит синтаксис JSON и отформатирует его для лучшей читаемости.
            </DescriptionText>

            <Toolbar>
              <ToolbarLeft>
                <ActionButton
                  variant="contained"
                  onClick={handleFormat}
                  disabled={!jsonInput.trim() || isValid === false}
                >
                  Форматировать
                </ActionButton>
                <ActionButton
                  variant="contained"
                  onClick={handleMinify}
                  disabled={!jsonInput.trim() || isValid === false}
                >
                  Минифицировать
                </ActionButton>
                <ActionButton
                  variant="outlined"
                  onClick={handleClear}
                  disabled={!jsonInput.trim()}
                >
                  Очистить
                </ActionButton>
              </ToolbarLeft>
              <ToolbarRight>
                <IndentLabel>Отступы:</IndentLabel>
                <IndentSelector
                  value={indentSize}
                  onChange={(e) => setIndentSize(Number(e.target.value) as 2 | 4)}
                >
                  <option value={2}>2 пробела</option>
                  <option value={4}>4 пробела</option>
                </IndentSelector>
              </ToolbarRight>
            </Toolbar>

            <TwoPanelLayout>
              <Panel>
                <PanelHeader>
                  <PanelTitle>Входные данные</PanelTitle>
                  <PanelActions>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json,application/json"
                      style={{ display: "none" }}
                      onChange={handleFileUpload}
                    />
                    <CopyButton
                      variant="outlined"
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Загрузить файл
                    </CopyButton>
                  </PanelActions>
                </PanelHeader>
                <CodeMirrorWrapper $hasError={isValid === false}>
                  <CodeMirror
                    value={jsonInput}
                    height="500px"
                    extensions={[json()]}
                    onChange={(value) => {
                      setJsonInput(value);
                      setFormattedJson("");
                      setStats(null);
                      setIsValid(null);
                      setError("");
                    }}
                    theme="light"
                    basicSetup={{
                      lineNumbers: true,
                      highlightActiveLineGutter: true,
                      highlightSpecialChars: true,
                      foldGutter: true,
                      drawSelection: true,
                      dropCursor: true,
                      indentOnInput: true,
                      syntaxHighlighting: true,
                      bracketMatching: true,
                      closeBrackets: true,
                      autocompletion: true,
                      highlightActiveLine: true,
                      highlightSelectionMatches: true,
                    }}
                    placeholder='{"name": "John", "age": 30}'
                  />
                </CodeMirrorWrapper>
                {isValid === false && error && <ErrorMessage>{error}</ErrorMessage>}
                {isValid === true && <SuccessMessage>✓ JSON валиден</SuccessMessage>}
              </Panel>

              <Panel>
                <PanelHeader>
                  <PanelTitle>Результат</PanelTitle>
                  <PanelActions>
                    {formattedJson && (
                      <>
                        <CopyButton variant="outlined" size="small" onClick={handleCopy}>
                          Копировать
                        </CopyButton>
                        <CopyButton variant="outlined" size="small" onClick={handleDownload}>
                          Скачать .json
                        </CopyButton>
                      </>
                    )}
                  </PanelActions>
                </PanelHeader>
                <CodeMirrorWrapper $hasSuccess={isValid === true && !!formattedJson}>
                  <CodeMirror
                    value={formattedJson}
                    height="500px"
                    extensions={[json()]}
                    editable={false}
                    theme="light"
                    basicSetup={{
                      lineNumbers: true,
                      highlightActiveLineGutter: false,
                      foldGutter: true,
                      syntaxHighlighting: true,
                      bracketMatching: true,
                    }}
                    placeholder="Результат форматирования появится здесь..."
                  />
                </CodeMirrorWrapper>
              </Panel>
            </TwoPanelLayout>

            {stats && (
              <StatsContainer>
                <StatItem>
                  <strong>Размер:</strong> {stats.size} байт
                </StatItem>
                <StatItem>
                  <strong>Строк:</strong> {stats.lines}
                </StatItem>
                <StatItem>
                  <strong>Ключей:</strong> {stats.keys}
                </StatItem>
                <StatItem>
                  <strong>Глубина:</strong> {stats.depth}
                </StatItem>
              </StatsContainer>
            )}
          </JsonValidatorContainer>
        </ContentWrapper>
      </ContentContainer>
    </Container>
  );
};
